
import { useEffect, useState } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  display_order: number | null;
  is_active: boolean | null;
}

export function FAQ() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFaqs();
  }, []);

  const loadFaqs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setFaqs(data || []);
    } catch (error) {
      console.error('Erro ao carregar FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section id="faq" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-gray-500">Carregando perguntas frequentes...</p>
          </div>
        </div>
      </section>
    );
  }

  if (faqs.length === 0) {
    return null;
  }

  // Dividir as perguntas em duas colunas
  const firstColumn = faqs.slice(0, Math.ceil(faqs.length / 2));
  const secondColumn = faqs.slice(Math.ceil(faqs.length / 2));

  return (
    <section id="faq" className="py-20 bg-[#f4fbf7]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-14">
          <p className="section-label mb-3">Dúvidas Frequentes</p>
          <h2 className="section-heading mb-3">Perguntas Frequentes</h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto font-primary font-light">
            Tire suas dúvidas sobre os atendimentos
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Primeira Coluna */}
            <div className="space-y-3">
              <Accordion type="single" collapsible>
                {firstColumn.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-white rounded-xl border border-gray-100 px-5 shadow-sm data-[state=open]:shadow-md data-[state=open]:border-brand-primary/20 transition-all"
                  >
                    <AccordionTrigger className="text-left font-bold font-primary text-sm text-gray-800 hover:text-brand-primary hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-500 font-primary font-light text-sm leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Segunda Coluna */}
            <div className="space-y-3">
              <Accordion type="single" collapsible>
                {secondColumn.map((faq, index) => (
                  <AccordionItem
                    key={index + firstColumn.length}
                    value={`item-${index + firstColumn.length}`}
                    className="bg-white rounded-xl border border-gray-100 px-5 shadow-sm data-[state=open]:shadow-md data-[state=open]:border-brand-primary/20 transition-all"
                  >
                    <AccordionTrigger className="text-left font-bold font-primary text-sm text-gray-800 hover:text-brand-primary hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-500 font-primary font-light text-sm leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
